import { put, call, takeLatest } from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import { ClaimItemsApi, ClaimItemCorrectionsApi, ClaimItemDTO } from '@reposit/api-client';

import {
  CREATE_CLAIM_ITEM_API_REQUESTED,
  createClaimItemSuccess,
  ClaimItemActionTypes,
  createClaimItemFailed,
  DISCARD_CLAIM_ITEM_API_REQUESTED,
  discardClaimItemSuccess,
  discardClaimItemFailed,
  updateClaimItemSuccess,
  updateClaimItemFailed,
  UPDATE_CLAIM_ITEM_API_REQUESTED,
  CREATE_CLAIM_ITEM_STORE_KEY,
  DISCARD_CLAIM_ITEM_STORE_KEY,
  UPDATE_CLAIM_ITEM_STORE_KEY,
  INTERNAL_UPDATE_CLAIM_ITEM_API_REQUESTED,
  INTERNAL_UPDATE_CLAIM_ITEM_STORE_KEY,
  internalUpdateClaimItemSuccess,
  internalUpdateClaimItemFailed,
  PREVIEW_CLAIM_ITEM_UPDATE_API_REQUESTED,
  PREVIEW_CLAIM_ITEM_UPDATE_STORE_KEY,
  previewClaimItemUpdateSuccess,
  previewClaimItemUpdateFailed,
} from './claim-item.actions';
import { runSagaWithAuth, createClaimItemCorrectionsApi, createClaimItemsApi } from '../utils/api.utils';
import SCHEMA from '../schema';
import { syncEntitiesAndGetResults } from '../entities/entities.sagas';
import { getErrorMessage } from '../../utils/common.utils';
import {
  UpdateClaimItemPayload,
  CreateClaimItemPayload,
  DiscardClaimItemPayload,
  InternalUpdateClaimItemPayload,
  PreviewClaimItemUpdatePayload,
} from './claim-item.types';
import { FlashMessagesActionTypes, setFlashMessage } from '../flash-messages/flash-messages.actions';
import { FlashState } from '../../components/FlashMessage/index';
import { ClaimActionTypes, fetchClaimRequested } from '../claim/claim.actions';

export function* createClaimItem({ payload }: { type: string; payload: CreateClaimItemPayload }) {
  try {
    const claimItemsApi: ClaimItemsApi = yield createClaimItemsApi();
    const apiResponse: AxiosResponse<ClaimItemDTO> = yield call(
      runSagaWithAuth(() => claimItemsApi.createClaimItem(payload.claimId, payload.data))
    );
    yield syncEntitiesAndGetResults(apiResponse.data, SCHEMA.claimItem);
    yield put<ClaimItemActionTypes>(createClaimItemSuccess(apiResponse.data));
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({
        key: CREATE_CLAIM_ITEM_STORE_KEY,
        message: 'Success! You have add a claim item.',
        state: FlashState.SUCCESS,
      })
    );
  } catch (e) {
    const error = getErrorMessage(e);
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({ key: CREATE_CLAIM_ITEM_STORE_KEY, message: error, state: FlashState.ERROR })
    );
    yield put<ClaimItemActionTypes>(createClaimItemFailed(error));
  }
}

export function* updateClaimItem({ payload }: { type: string; payload: UpdateClaimItemPayload }) {
  try {
    const claimItemsApi: ClaimItemsApi = yield createClaimItemsApi();
    const apiResponse: AxiosResponse<ClaimItemDTO> = yield call(
      runSagaWithAuth(() => claimItemsApi.updateClaimItem(payload.claimId, payload.claimItemId, payload.data))
    );
    yield syncEntitiesAndGetResults(apiResponse.data, SCHEMA.claimItem);
    yield put<ClaimItemActionTypes>(updateClaimItemSuccess(apiResponse.data));
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({
        key: UPDATE_CLAIM_ITEM_STORE_KEY,
        message: 'Success! You have updated a claim item.',
        state: FlashState.SUCCESS,
      })
    );
  } catch (e) {
    const error = getErrorMessage(e);
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({ key: UPDATE_CLAIM_ITEM_STORE_KEY, message: error, state: FlashState.ERROR })
    );
    yield put<ClaimItemActionTypes>(updateClaimItemFailed(error));
  }
}

export function* discardClaimItem({ payload }: { type: string; payload: DiscardClaimItemPayload }) {
  try {
    const claimItemsApi: ClaimItemsApi = yield createClaimItemsApi();
    yield call(runSagaWithAuth(() => claimItemsApi.deleteClaimItem(payload.claimId, payload.claimItemId)));
    yield put<ClaimItemActionTypes>(discardClaimItemSuccess(payload));
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({
        key: DISCARD_CLAIM_ITEM_STORE_KEY,
        message: 'Success! You have removed a claim item.',
        state: FlashState.SUCCESS,
      })
    );
  } catch (e) {
    const error = getErrorMessage(e);
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({ key: DISCARD_CLAIM_ITEM_STORE_KEY, message: error, state: FlashState.ERROR })
    );
    yield put<ClaimItemActionTypes>(discardClaimItemFailed(error));
  }
}

export function* internalUpdateClaimItem({ payload }: { type: string; payload: InternalUpdateClaimItemPayload }) {
  try {
    const claimItemCorrectionsApi: ClaimItemCorrectionsApi = yield createClaimItemCorrectionsApi();
    const successMessage =
      payload.data.newAmount === 0 ? 'Success! You have deleted a claim item.' : 'Success! You have updated a claim item.';
    const apiResponse = yield call(
      runSagaWithAuth(() => claimItemCorrectionsApi.applyClaimItemCorrection(payload.claimItemId, payload.data))
    );
    yield put<ClaimItemActionTypes>(internalUpdateClaimItemSuccess(apiResponse.data));
    yield put<ClaimActionTypes>(fetchClaimRequested({ claimId: payload.claimId }));
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({
        key: INTERNAL_UPDATE_CLAIM_ITEM_STORE_KEY,
        message: successMessage,
        state: FlashState.SUCCESS,
      })
    );
  } catch (e) {
    const error = getErrorMessage(e);
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({ key: INTERNAL_UPDATE_CLAIM_ITEM_STORE_KEY, message: error, state: FlashState.ERROR })
    );
    yield put<ClaimItemActionTypes>(internalUpdateClaimItemFailed(error));
  }
}

export function* previewClaimItemUpdate({ payload }: { type: string; payload: PreviewClaimItemUpdatePayload }) {
  try {
    const claimItemCorrectionsApi: ClaimItemCorrectionsApi = yield createClaimItemCorrectionsApi();
    const apiResponse = yield call(
      runSagaWithAuth(() => claimItemCorrectionsApi.dryRunClaimItemCorrection(payload.claimItemId, payload.data))
    );
    yield put<ClaimItemActionTypes>(
      previewClaimItemUpdateSuccess({ payload, preview: apiResponse.data })
    );
  } catch (e) {
    const error = getErrorMessage(e);
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({ key: PREVIEW_CLAIM_ITEM_UPDATE_STORE_KEY, message: error, state: FlashState.ERROR })
    );
    yield put<ClaimItemActionTypes>(previewClaimItemUpdateFailed(error));
  }
}

// ****************
// WATCHERS
// ****************
export function* watchClaimItemsSagas() {
  yield takeLatest(CREATE_CLAIM_ITEM_API_REQUESTED, createClaimItem);
  yield takeLatest(UPDATE_CLAIM_ITEM_API_REQUESTED, updateClaimItem);
  yield takeLatest(PREVIEW_CLAIM_ITEM_UPDATE_API_REQUESTED, previewClaimItemUpdate);
  yield takeLatest(INTERNAL_UPDATE_CLAIM_ITEM_API_REQUESTED, internalUpdateClaimItem);
  yield takeLatest(DISCARD_CLAIM_ITEM_API_REQUESTED, discardClaimItem);
}
